// Insert delivery transformations into a Cloudinary URL. We don't take
// cloudName as a parameter; we just rewrite the upload segment of an
// existing URL so the helper works wherever the API hands us a CDN link.
//
// Example input:  https://res.cloudinary.com/abc/image/upload/v123/igs/photos/x.jpg
// Example output: https://res.cloudinary.com/abc/image/upload/f_auto,q_auto,w_400/v123/igs/photos/x.jpg

const UPLOAD_SEGMENT = /\/(image|video)\/upload\//;

const W_SM = 400;
const W_MD = 640;
const W_LG = 960;
const W_XL = 1280;
const W_XXL = 1600;
const RESPONSIVE_WIDTHS = [W_SM, W_MD, W_LG, W_XL, W_XXL] as const;
const DEFAULT_DISPLAY_WIDTH = 800;

export function withTransform(url: string, transform: string): string {
  return url.replace(UPLOAD_SEGMENT, (match) => `${match}${transform}/`);
}

export function imageSrc(url: string, width: number = DEFAULT_DISPLAY_WIDTH): string {
  return withTransform(url, `f_auto,q_auto,w_${String(width)},c_limit`);
}

export function imageSrcSet(url: string): string {
  return RESPONSIVE_WIDTHS.map((w) => `${imageSrc(url, w)} ${String(w)}w`).join(', ');
}

export function imageSizes(): string {
  // Photo grid: roughly 1 column phones, 2 small tablets, 3 desktop+. Tune
  // alongside the grid CSS in PhotoGrid.tsx if the layout changes.
  return '(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw';
}

export function videoPosterSrc(posterUrl: string): string {
  if (!posterUrl) return '';
  return withTransform(posterUrl, 'f_auto,q_auto,w_960,c_limit');
}

// Generate a still-frame thumbnail from a Cloudinary *video* URL: swap the file
// extension for .jpg so Cloudinary renders a frame, and let so_auto pick a
// representative (non-blank) frame. Returns '' for empty input.
export function videoPosterFromUrl(videoUrl: string): string {
  if (!videoUrl) return '';
  const asJpg = videoUrl.replace(/\.[a-z0-9]+(?=$|\?)/i, '.jpg');
  return withTransform(asJpg, 'so_auto,f_auto,q_auto,w_960,c_limit');
}

export { RESPONSIVE_WIDTHS };
